import { create } from 'zustand'
import type { PartOutcome, PartTarget } from './wide-replace'

/** 'section' on prose only, 'slide' on a deck only. */
export type FindScope = 'section' | 'slide' | 'part' | 'artefact' | 'bid'

/** A wide scope's count (or Replace all report): the parts in scope order and what each gave, for one `key`. */
export interface WideRun {
  /** What it was run for: scope, case, the scope's root (artefact or bid) and the query. */
  key: string
  targets: PartTarget[]
  outcomes: PartOutcome[]
}

/** The Find panel's session: what survives the room change Find itself causes (AB#366). */
export interface FindSession {
  /** The bid it belongs to. */
  bid: string | null
  query: string
  replacement: string
  caseSensitive: boolean
  scope: FindScope
  /** The wide scope's latest count. */
  count: WideRun | null
  /** The latest wide Replace all's report. */
  report: WideRun | null
  /** A room Find opened to go to a match, and which one: its first (↓, a results row) or its last
   *  (↑). Canvas keeps the panel open over that room change, and the panel, re-bound to the room
   *  once it has synced, selects the match and clears this. */
  pending: { room: string; at: 'first' | 'last' } | null
}

interface FindStore extends FindSession {
  /** A fresh session (the panel opened anew): the defaults, `query` seeded. */
  start: (bid: string | null, query: string) => void
  save: (patch: Partial<FindSession>) => void
  clearPending: () => void
}

const FRESH: Omit<FindSession, 'bid' | 'query'> = {
  replacement: '',
  caseSensitive: false,
  scope: 'artefact',
  count: null,
  report: null,
  pending: null,
}

/**
 * The Find/Replace panel's state, outside the panel (AB#366). The panel is unmounted while the part
 * it sent the member to opens (Canvas has no connection for a moment), and remounts on the new
 * room: it resumes from here when it finds a `pending` navigation to that room, and starts afresh
 * otherwise. One session at a time — a bid switch is a room change Find did not cause, which closes
 * the panel; the next open starts a new session for that bid.
 */
export const useFindStore = create<FindStore>((set) => ({
  bid: null,
  query: '',
  ...FRESH,
  start: (bid, query) => set({ bid, query, ...FRESH }),
  save: (patch) => set(patch),
  clearPending: () => set({ pending: null }),
}))
